import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Pencil, Palette, Trash2, KanbanSquare } from 'lucide-react'

import { productionBasePath } from '@/features/studio/lib/production-path'
import { ImagePreviewModal } from '@/features/studio/modals/ImagePreviewModal'
import { AssignWorkItemModal, type AssignWorkItemPreset } from '@/features/studio/modals/AssignWorkItemModal'
import type { CollaborationGate } from '@/features/studio/lib/collaboration-gate'
import type { ProductionDepartment, ProductionMember } from '@/api/studio-production'
import type { BibleEntryKind, BibleEntryLike } from '@/features/studio/pages/bible/bible-utils'
import {
  bibleAllowsMultipleImages,
  bibleReferenceUrls,
  bibleRefPublicIdForEntry,
  bibleRefTypeForKind,
  bibleKindLabelKey,
  bibleCanvasPath,
  BIBLE_STYLE_ENTRY_IDS,
  createCanvasLabelKey,
  uploadAssetKindTag,
} from '@/features/studio/pages/bible/bible-utils'
import { resolveMediaDisplayUrl } from '@/features/studio/lib/media-display'
import { studioCatalogCardShellClass } from '@/features/studio/components/StudioCatalogCard'
import { CatalogListCardContent } from '@/shared/components/catalog/CatalogListCardContent'
import { CATALOG_LIST_COVER_FALLBACK_ASPECT } from '@/shared/components/catalog/catalogListGrid'
import { ProtoThumb, ProtoBtn, protoBtnClassName } from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'

function BibleImage({
  url,
  seed,
  className,
  onClick,
}: {
  url?: string
  seed: string
  className?: string
  onClick?: () => void
}) {
  const [failed, setFailed] = useState(false)
  const src = url ? resolveMediaDisplayUrl(url) : ''

  useEffect(() => {
    setFailed(false)
  }, [url])

  if (!src || failed) {
    const Tag = onClick ? Button : 'div'
    return (
      <Tag
        {...(onClick
          ? { type: 'button' as const, variant: 'ghost' as const, className: `h-full w-full overflow-hidden rounded-none bg-[#0e0e16] p-0 ${className ?? ''}`, onClick }
          : { className: `h-full w-full overflow-hidden rounded-none bg-[#0e0e16] ${className ?? ''}` })}
      >
        <ProtoThumb seed={seed} aspect="square" className="h-full w-full" flush />
      </Tag>
    )
  }

  return (
    <Button type="button" variant="ghost" onClick={onClick} className={`h-full w-full overflow-hidden rounded-none bg-[#0e0e16] p-0 ${className ?? ''}`}>
      <img
        src={src}
        alt=""
        className="h-full w-full object-cover"
        onError={() => setFailed(true)}
      />
    </Button>
  )
}

export function BibleEntryCard({
  productionId,
  kind,
  entry,
  onEdit,
  onDelete,
  canAssignWork,
  canEditEntry = true,
  gate,
  members,
  departments,
  onAssigned,
}: {
  productionId: string
  kind: BibleEntryKind
  entry: BibleEntryLike
  onEdit: () => void
  onDelete?: () => void
  canAssignWork?: boolean
  canEditEntry?: boolean
  gate?: CollaborationGate
  members?: ProductionMember[]
  departments?: ProductionDepartment[]
  onAssigned?: () => void
}) {
  const { t } = useTranslation()
  const urls = bibleReferenceUrls(entry)
  const multipleImages = bibleAllowsMultipleImages(kind)
  const [previewIndex, setPreviewIndex] = useState<number | null>(null)
  const [assignOpen, setAssignOpen] = useState(false)

  const createLabel = t(createCanvasLabelKey(kind))
  const isStyleArtCard = kind !== 'style' || entry.public_id === BIBLE_STYLE_ENTRY_IDS.art_direction
  const canvasHref = isStyleArtCard
    ? bibleCanvasPath(
        productionId,
        kind,
        entry,
        `${productionBasePath(productionId)}/bible`,
      )
    : null
  const heroUrl = urls[0]
  const thumbUrls = urls.slice(1, 4)
  const extraCount = Math.max(0, urls.length - 4)

  function openPreview(index = 0) {
    if (urls.length === 0) return
    setPreviewIndex(index)
  }

  const assignPreset: AssignWorkItemPreset = {
    bibleRefType: bibleRefTypeForKind(kind),
    bibleRefPublicId: bibleRefPublicIdForEntry(kind, entry.public_id),
    expectedKind: uploadAssetKindTag(kind),
    title: `${entry.name} · ${t(bibleKindLabelKey(kind))}`,
  }

  return (
    <>
      <article className={studioCatalogCardShellClass('flex h-full flex-col overflow-hidden')}>
        <div className={`relative w-full ${CATALOG_LIST_COVER_FALLBACK_ASPECT} shrink-0 overflow-hidden`}>
          <BibleImage
            url={heroUrl}
            seed={`${entry.public_id}-hero`}
            className="h-full w-full object-contain"
            onClick={urls.length > 0 ? () => openPreview(0) : undefined}
          />
          {multipleImages && urls.length > 1 ? (
            <div className="absolute bottom-2 left-2 flex gap-1">
              {thumbUrls.map((url, i) => (
                <BibleImage
                  key={`${url}-${i + 1}`}
                  url={url}
                  seed={`${entry.public_id}-thumb-${i}`}
                  className="h-8 w-8 rounded border border-black/40"
                  onClick={() => openPreview(i + 1)}
                />
              ))}
              {extraCount > 0 ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => openPreview(3)}
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded border border-white/[0.12] bg-black/50 p-0 text-[10px] text-zinc-300 hover:text-indigo-200"
                >
                  +{extraCount}
                </Button>
              ) : null}
            </div>
          ) : null}
          <div className="absolute right-2 top-2 flex overflow-hidden rounded-lg border border-white/20 bg-[#0e0e16]/95 shadow-[0_4px_16px_rgba(0,0,0,0.45)] backdrop-blur-sm">
            {canEditEntry ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={onEdit}
                title={t('bible.editEntryShort')}
                className="rounded-none border-r border-white/10 text-zinc-100 hover:bg-indigo-500/20 hover:text-indigo-100"
              >
                <Pencil className="h-4 w-4" strokeWidth={2} />
              </Button>
            ) : null}
            {canEditEntry && onDelete ? (
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={onDelete}
                title={t('bible.delete')}
                className="rounded-none text-rose-300 hover:bg-rose-500/20 hover:text-rose-100"
              >
                <Trash2 className="h-4 w-4" strokeWidth={2} />
              </Button>
            ) : null}
          </div>
        </div>

        <CatalogListCardContent
          className="!aspect-auto flex-1 min-h-[72px]"
          title={entry.name}
          summary={entry.description.trim() || undefined}
          hideFooter={!canvasHref && !(canAssignWork && gate && isStyleArtCard)}
          footer={
            canvasHref || (canAssignWork && gate && isStyleArtCard) ? (
              <div className="flex shrink-0 flex-wrap justify-end gap-1">
                {canAssignWork && gate && isStyleArtCard ? (
                  <ProtoBtn size="xs" variant="soft" tone="sky" onClick={() => setAssignOpen(true)}>
                    <KanbanSquare className="h-3 w-3" />
                    {t('bible.assignWork')}
                  </ProtoBtn>
                ) : null}
                {canvasHref ? (
                  <Link
                    to={canvasHref}
                    className={protoBtnClassName({ size: 'xs', variant: 'soft', tone: 'amber' })}
                  >
                    <Palette className="h-3 w-3" />
                    {createLabel}
                  </Link>
                ) : null}
              </div>
            ) : undefined
          }
        />
      </article>

      <ImagePreviewModal
        open={previewIndex != null}
        urls={urls}
        index={previewIndex ?? 0}
        title={entry.name}
        onClose={() => setPreviewIndex(null)}
        onIndexChange={setPreviewIndex}
      />

      {canAssignWork && gate && members && departments && isStyleArtCard ? (
        <AssignWorkItemModal
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
          productionId={productionId}
          members={members}
          departments={departments}
          preset={assignPreset}
          gate={gate}
          onAssigned={onAssigned}
        />
      ) : null}
    </>
  )
}
